import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import {
  getCashCalcCredentialsList,
  upsertCashCalcCredential,
  deleteCashCalcCredential,
} from "../../../../redux/SystemManager/cashcalcCredentialsSlice";
import { getUsersListItems } from "../../../../redux/SystemManager/manageUser";
import { useToastMessage } from "../../../../components/ToastMessage/ToastMessage";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Input,
  makeStyles,
  Select,
  Spinner,
  tokens,
  Tooltip,
  Toaster,
} from "@fluentui/react-components";
import { Edit16Filled, Delete16Filled } from "@fluentui/react-icons";

// All cells: 16px horizontal padding.
// Edge cells (first col left, last col right) get 32px = 2×16 so that
// outer margin equals the inter-column gap (16 + 16).
const P = "16px";
const EDGE = "32px";
const V = "10px";

const useStyles = makeStyles({
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" },
  title: { fontSize: "18px", fontWeight: "600" },
  tableWrapper: { overflowX: "auto" },
  formField: { marginBottom: "12px" },
  actionButtons: { display: "flex", gap: "4px" },
  viewLabel: { fontWeight: 600, minWidth: "120px", display: "inline-block", flexShrink: 0 },
  viewRow: { display: "flex", gap: "12px", alignItems: "flex-start" },
  tableRow: {
    ":hover": { backgroundColor: tokens.colorNeutralBackground2Hover },
  },
});

const cellStyle = (first?: boolean, last?: boolean, header?: boolean): React.CSSProperties => ({
  padding: `${V} ${last ? EDGE : P} ${V} ${first ? EDGE : P}`,
  textAlign: "left",
  fontWeight: header ? 700 : undefined,
  borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  color: header ? tokens.colorNeutralForeground1 : undefined,
  fontSize: "14px",
  verticalAlign: "middle",
});

const emptyForm = { cashCalcUsername: "", clientId: "", clientSecret: "" };

const CashCalcCredentialsPage: React.FC = () => {
  const styles = useStyles();
  const dispatch = useDispatch<AppDispatch>();
  const { showToast: notify, toasterId } = useToastMessage();

  const { listItems, loading, saveLoading } = useSelector((state: RootState) => state.cashcalcCredentials);
  const { listItems: userList, loading: userListLoading } = useSelector((state: RootState) => state.manageUser);

  // Wait for the user list too, not just the credentials list — otherwise the table can
  // render before userLabel() has anything to resolve UserId against, briefly showing the
  // raw UserId until userList finishes loading a moment later.
  const isLoading = loading || userListLoading;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [originalUserId, setOriginalUserId] = useState("");
  const [deletingUserId, setDeletingUserId] = useState("");
  const [form, setForm] = useState({ ...emptyForm });
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    dispatch(getCashCalcCredentialsList());
    dispatch(getUsersListItems());
  }, [dispatch]);

  const configuredIds = new Set(listItems.map((i: any) => i.UserId));
  // Users selectable in the dialog's "User" dropdown — anyone without a credential yet,
  // plus (in edit mode only) the user currently being edited, so their own entry doesn't
  // disappear from their own dialog. Deliberately excludes every OTHER already-configured
  // user in edit mode too, not just add mode — reassigning onto one of them would silently
  // delete their credential and overwrite it with this dialog's values.
  const selectableUsers = userList.filter(
    (u: any) => !configuredIds.has(u.user_id) || (isEdit && u.user_id === originalUserId)
  );

  const openAddDialog = () => {
    setIsEdit(false);
    setSelectedUserId("");
    setOriginalUserId("");
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEditDialog = (item: any) => {
    setIsEdit(true);
    setSelectedUserId(item.UserId);
    setOriginalUserId(item.UserId);
    setForm({
      cashCalcUsername: item.CashCalcUsername || "",
      clientId: item.ClientId || "",
      clientSecret: item.ClientSecret || "",
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (userId: string) => {
    setDeletingUserId(userId);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedUserId) { notify("Validation Error", "Please select a user.", "error"); return; }
    if (!form.clientId) { notify("Validation Error", "Client Id is required.", "error"); return; }
    if (!form.clientSecret) { notify("Validation Error", "Client Secret is required.", "error"); return; }

    if (isEdit && originalUserId && originalUserId !== selectedUserId) {
      const deleteResult = await dispatch(deleteCashCalcCredential(originalUserId));
      if (!deleteCashCalcCredential.fulfilled.match(deleteResult)) {
        // Don't proceed to create a new record under selectedUserId if we couldn't
        // confirm the old one under originalUserId was actually removed — otherwise a
        // failed delete plus a successful upsert leaves two rows for one reassignment.
        notify("Error", "Failed to remove the previous account — nothing was changed.", "error");
        return;
      }
    }

    const result = await dispatch(upsertCashCalcCredential({ userId: selectedUserId, data: form }));
    if (upsertCashCalcCredential.fulfilled.match(result)) {
      notify("Saved", `Account ${isEdit ? "updated" : "added"} successfully.`);
      setDialogOpen(false);
      dispatch(getCashCalcCredentialsList());
    } else {
      notify("Error", "Failed to save account.", "error");
    }
  };

  const handleDelete = async () => {
    const result = await dispatch(deleteCashCalcCredential(deletingUserId));
    if (deleteCashCalcCredential.fulfilled.match(result)) {
      notify("Deleted", "CashCalc account removed.");
      setDeleteDialogOpen(false);
    } else {
      notify("Error", "Failed to remove account.", "error");
    }
  };

  const userLabel = (userId: string) => {
    const u = userList.find((x: any) => x.user_id === userId);
    return u ? u.full_name || u.user_email || userId : userId;
  };

  return (
    <div style={{ padding: "20px" }}>
      <Toaster toasterId={toasterId} position="top-end" timeout={5000} />

      <div className={styles.header}>
        <span className={styles.title}>CashCalc Accounts</span>
        <Button
          appearance="primary"
          className="asc-button-primary"
          shape="rounded"
          size="medium"
          onClick={openAddDialog}
        >
          Add Account
        </Button>
      </div>

      {isLoading ? (
        <Spinner label="Loading…" />
      ) : (
        <div className={styles.tableWrapper}>
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: "18%" }} />
              <col style={{ width: "25%" }} />
              <col style={{ width: "25%" }} />
              <col style={{ width: "25%" }} />
              <col style={{ width: "7%" }} />
            </colgroup>
            <thead>
              <tr>
                <th style={cellStyle(true, false, true)}>User</th>
                <th style={cellStyle(false, false, true)}>Username</th>
                <th style={cellStyle(false, false, true)}>Client Id</th>
                <th style={cellStyle(false, false, true)}>Client Secret</th>
                <th style={cellStyle(false, true, true)}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {listItems.length === 0 ? (
                <tr>
                  <td colSpan={5} style={cellStyle(true, true)}>
                    No accounts configured yet.
                  </td>
                </tr>
              ) : (
                listItems.map((item: any) => (
                  <tr key={item.UserId} className={styles.tableRow}>
                    <td style={cellStyle(true, false)}>{userLabel(item.UserId)}</td>
                    <td style={cellStyle(false, false)}>{item.CashCalcUsername || "—"}</td>
                    <td style={cellStyle(false, false)}>
                      <span style={{ fontFamily: "monospace", wordBreak: "break-all", fontSize: "13px" }}>
                        {item.ClientId || "—"}
                      </span>
                    </td>
                    <td style={cellStyle(false, false)}>
                      <span style={{ fontFamily: "monospace", wordBreak: "break-all", fontSize: "13px" }}>
                        {item.ClientSecret || "—"}
                      </span>
                    </td>
                    <td style={cellStyle(false, true)}>
                      <div className={styles.actionButtons}>
                        <Tooltip content="Edit" relationship="label" positioning="below">
                          <Button
                            shape="rounded"
                            appearance="subtle"
                            icon={<Edit16Filled primaryFill="var(--button-color)" />}
                            onClick={() => openEditDialog(item)}
                          />
                        </Tooltip>
                        <Tooltip content="Delete" relationship="label" positioning="below">
                          <Button
                            shape="rounded"
                            appearance="subtle"
                            icon={<Delete16Filled primaryFill="#d13438" />}
                            onClick={() => openDeleteDialog(item.UserId)}
                          />
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(_, d) => setDialogOpen(d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{isEdit ? "Edit CashCalc Account" : "Add CashCalc Account"}</DialogTitle>
            <DialogContent>
              <div className={styles.formField}>
                <Field label="User" required>
                  <Select
                    value={selectedUserId}
                    onChange={(_, d) => setSelectedUserId(d.value)}
                  >
                    {!isEdit && <option value="">Select user…</option>}
                    {selectableUsers.map((u: any) => (
                      <option key={u.user_id} value={u.user_id}>
                        {u.full_name || u.user_email || u.user_id}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
              <div className={styles.formField}>
                <Field label="Username">
                  <Input
                    type="email"
                    value={form.cashCalcUsername}
                    onChange={(_, d) => setForm({ ...form, cashCalcUsername: d.value })}
                    placeholder="user@example.com"
                  />
                </Field>
              </div>
              <div className={styles.formField}>
                <Field label="Client Id" required>
                  <Input
                    value={form.clientId}
                    onChange={(_, d) => setForm({ ...form, clientId: d.value })}
                    placeholder="CashCalc Client Id"
                  />
                </Field>
              </div>
              <div className={styles.formField}>
                <Field label="Client Secret" required>
                  <Input
                    value={form.clientSecret}
                    onChange={(_, d) => setForm({ ...form, clientSecret: d.value })}
                    placeholder="CashCalc Client Secret"
                  />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancel</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                className="asc-button-primary"
                shape="rounded"
                size="medium"
                onClick={handleSave}
                disabled={saveLoading}
                icon={saveLoading ? <Spinner size="tiny" /> : undefined}
              >
                {saveLoading ? "Saving…" : "Save"}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(_, d) => setDeleteDialogOpen(d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Remove CashCalc Account</DialogTitle>
            <DialogContent>
              Are you sure you want to remove this CashCalc account? The user will need to reconfigure before connecting again.
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancel</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                onClick={handleDelete}
                style={{ backgroundColor: "#d13438", borderColor: "#d13438" }}
              >
                Remove
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default CashCalcCredentialsPage;
